import React, { useEffect, useRef } from 'react';
import { NumericFormat } from 'react-number-format';
import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import { changeFormFieldValue, removeFieldError, setFieldError } from 'apps/auth/actions/formAction';
import { CalculatorInputLabel, CalculatorInputWrapper } from 'apps/auth/components/common/Calculator/styles';
import { AMOUNT_TO } from 'apps/auth/constants/fieldsName';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import { resolveArguments } from 'apps/auth/helpers/utils';
import { useApp, useLanguage } from 'apps/auth/hooks/selectors';
import { useNumberSeparators } from 'apps/auth/hooks/useNumberSeparators';

const decimalScale = 2;

const CalculatorInputAmountForRequest = (props) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const amountRef = useRef(null);
    const timerID = useRef(null);
    const { thousandSeparator, decimalSeparator } = useNumberSeparators(lang, country);

    useEffect(() => {
        if (__isBrowser__ && props.focus && amountRef.current) {
            amountRef.current.focus();
            let caret = 0;
            if (props.amountField.value) {
                const fullLength = props.amountField.value.toString().length;
                const intLength = Math.ceil(Math.log10(props.amountField.value));
                caret = fullLength === intLength ? fullLength : fullLength - (decimalScale + 1);
            }
            amountRef.current.selectionStart = caret;
            amountRef.current.selectionEnd = caret;
        }
        return () => {
            clearTimeout(timerID.current);
        };
    }, []);

    useEffect(() => {
        if (props.amountField.value) {
            validateAmount(props.amountField.value);
        }
    }, [props.maxAmount, props.minAmount]);

    const validateAmount = (currentValue) => {
        if (currentValue > props.maxAmount || currentValue < props.minAmount) {
            let errorMessage = '';

            if (currentValue < props.minAmount) {
                errorMessage = resolveArguments(props.minValidError, [getLocalizedRawAmount(lang, country, props.minAmount), props.currencyCode]);
            } else if (currentValue > props.maxAmount) {
                errorMessage = resolveArguments(props.maxValidError, [getLocalizedRawAmount(lang, country, props.maxAmount), props.currencyCode]);
            }

            props.setFieldError(props.formName, AMOUNT_TO, errorMessage);
        } else {
            props.removeFieldError(props.formName, AMOUNT_TO);
        }
    };

    const handlerChange = (value) => {
        const floatValue = value.floatValue;
        validateAmount(floatValue);

        props.changeFormFieldValue(props.formName, floatValue, AMOUNT_TO);
        props.updateFontSize?.();
    };

    const handlerBlur = (e) => {
        const value = e.target.value;
        const field = props.amountField;
        if (props.onBlurHandler) {
            props.onBlurHandler(value, field.name, field.checkRegexp, field.mandatory);
        }
    };

    return (
        <>
            <CalculatorInputLabel size="secondary" color="secondary">
                {props.title}
            </CalculatorInputLabel>
            <CalculatorInputWrapper $isError={props.amountField.isError}>
                <NumericFormat
                    getInputRef={amountRef}
                    id={props.amountField.name}
                    maxLength="14"
                    name={props.amountField.canonicalPath ? props.amountField.canonicalPath : props.amountField.name}
                    value={props.amountField.value}
                    defaultValue={'0'}
                    type={'tel'}
                    readOnly={props.isShowSpinner}
                    style={{ fontSize: props.customFontSize ? props.customFontSize : '' }}
                    autoComplete="off"
                    allowNegative={false}
                    thousandSeparator={thousandSeparator}
                    decimalSeparator={decimalSeparator}
                    fixedDecimalScale={true}
                    decimalScale={props.amountField.value ? decimalScale : false}
                    onValueChange={handlerChange}
                    onBlur={handlerBlur}
                />
            </CalculatorInputWrapper>
        </>
    );
};

CalculatorInputAmountForRequest.propTypes = {
    formName: PropTypes.string.isRequired,
    amountField: PropTypes.object.isRequired,
    ccyField: PropTypes.object,
    title: PropTypes.string.isRequired,
    restriction: PropTypes.object,
    customValidate: PropTypes.func,
    customFontSize: PropTypes.string.isRequired,
    currencyCode: PropTypes.string,
    currency: PropTypes.object,
    displayArrows: PropTypes.bool,
    pocketBalance: PropTypes.number,
};

const mapStateToProps = ({ app, messages, form }, ownProps) => ({
    isShowSpinner: app.isShowSpinner,
    fields: form[ownProps.formName]?.fields,
    minValidError: messages.jsMessages['site.js.ga.validation.min.valid.error'],
    maxValidError: messages.jsMessages['site.js.ga.validation.max.valid.error'],
});

export default connect(mapStateToProps, {
    changeFormFieldValue,
    removeFieldError,
    setFieldError,
})(CalculatorInputAmountForRequest);
